// Create / view opportunity from the Product card header.

import { useEffect, useState } from "react";
import type { Lead } from "@/types/lead";
import {
  createOpportunityFromLead,
  getLinkedOpportunityIdForLead,
} from "@/lib/opportunity/opportunityClient";
import { hasLeadUuidId } from "@/lib/lead/locationScope";

const LEAVE_LEAD_CONFIRM =
  "Leave this lead and open the linked opportunity in Opportunities? Unsaved lead edits are already auto-saved locally.";

export function ProductCardOpportunityAction(props: {
  lead: Lead;
  productLine: string | null | undefined;
  onLinkedOpportunity: (opportunityId: string) => void;
  onViewOpportunity: (opportunityId: string) => void;
  setToast: (t: { message: string; type: "success" | "error" } | null) => void;
}) {
  const { lead, productLine, onLinkedOpportunity, onViewOpportunity, setToast } = props;
  const [busy, setBusy] = useState(false);
  const linkedId = lead.linked_opportunity_id ?? null;
  const hasProductLine = Boolean((productLine || "").trim());

  useEffect(() => {
    if (!hasLeadUuidId(lead) || linkedId) return;
    let cancelled = false;
    void getLinkedOpportunityIdForLead(lead.id).then((r) => {
      if (cancelled || !r.ok || !r.data.opportunity_id) return;
      onLinkedOpportunity(r.data.opportunity_id);
    });
    return () => {
      cancelled = true;
    };
  }, [lead.id, linkedId, onLinkedOpportunity]);

  if (!hasLeadUuidId(lead)) return null;

  const onCreate = async () => {
    if (!hasProductLine) return;
    setBusy(true);
    try {
      const r = await createOpportunityFromLead({ lead_id: lead.id });
      if (r.ok) {
        onLinkedOpportunity(r.data.opportunity.id);
        setToast({ message: "Opportunity created from lead.", type: "success" });
        return;
      }
      const conflictId =
        r.status === 409 && r.payload && typeof r.payload.opportunity_id === "string"
          ? r.payload.opportunity_id
          : null;
      if (conflictId) {
        onLinkedOpportunity(conflictId);
        setToast({ message: "This lead already has a linked opportunity.", type: "success" });
        return;
      }
      setToast({ message: r.error, type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const onView = () => {
    if (!linkedId) return;
    if (!window.confirm(LEAVE_LEAD_CONFIRM)) return;
    onViewOpportunity(linkedId);
  };

  if (linkedId) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={onView}
        className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
      >
        View opportunity
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={busy || !hasProductLine}
      title={hasProductLine ? undefined : "Select a product line first"}
      onClick={() => void onCreate()}
      className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {busy ? "Creating…" : "Create Opportunity"}
    </button>
  );
}
